const CHAT_LAST_SEEN_STORAGE_KEY_PREFIX = "izanagi.chatLastSeenAt";

/** chatId → そのチャットを最後に見たときの最終活動時刻 (サーバが返す ISO 文字列)。 */
export type ChatLastSeen = Readonly<Record<string, string>>;

/**
 * localStorage が使えない環境でも、タブを開いている間は既読を保つためのセッション内キャッシュ。
 * 読めた値もここへ載せるので、以降の読み出しはストレージに触らない。
 */
const sessionCache = new Map<string, ChatLastSeen>();

/** ストレージ障害のログはセッション中 1 回だけ出す (5 秒ごとのポーリングで積み上げない)。 */
let storageErrorLogged = false;

/** 既読時刻はユーザーごとに分ける。共有端末やアカウント切り替えで互いの既読を壊さない。 */
function storageKey(userId: string): string {
	return `${CHAT_LAST_SEEN_STORAGE_KEY_PREFIX}.${userId}`;
}

function logStorageErrorOnce(message: string, err: unknown): void {
	if (storageErrorLogged) return;
	storageErrorLogged = true;
	console.error(message, err);
}

function parseChatLastSeen(raw: string): ChatLastSeen | null {
	const parsed: unknown = JSON.parse(raw);
	if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
	return Object.fromEntries(
		Object.entries(parsed).filter(([, value]) => typeof value === "string"),
	) as ChatLastSeen;
}

/**
 * 保存済みの既読時刻。null は「このブラウザにまだ既読情報が無い」で、呼び出し元は全チャットを
 * 既読として種をまく (初回ログインで一覧が未読の丸で埋まらないようにする)。
 *
 * localStorage が使えない・保存値が壊れている場合も最初の 1 回は null を返すが、以降は
 * セッション内キャッシュが答えるため、未読とエラーの丸はタブを開いている間ずっと正しく動く。
 * (恒久的に丸が出なくなるのを避ける。有効範囲はブラウザを閉じるまで。)
 *
 * エラーハンドリング規約 (再スロー必須) の明示的な例外: 呼び出し元は useChatActivitySync の
 * sync() だけで、再スローするとその catch に落ちて丸の更新ごと飛ぶ。localStorage が無効な環境で
 * 丸が一切出なくなり、通信エラーの文言でトーストが出る方が実害が大きい。
 */
export function readChatLastSeen(userId: string): ChatLastSeen | null {
	const cached = sessionCache.get(userId);
	if (cached !== undefined) return cached;
	const parsed = readStored(userId);
	if (parsed !== null) sessionCache.set(userId, parsed);
	return parsed;
}

/** セッション内キャッシュを介さず保存値を読む。null は未保存・破損・localStorage 無効。 */
function readStored(userId: string): ChatLastSeen | null {
	try {
		const raw = window.localStorage.getItem(storageKey(userId));
		return raw === null ? null : parseChatLastSeen(raw);
	} catch (err: unknown) {
		// localStorage が無効。セッション内キャッシュだけで続行する。
		logStorageErrorOnce("readChatLastSeen failed", err);
		return null;
	}
}

/**
 * 保存値と新しい既読時刻を突き合わせ、チャットごとに新しい方を残す。
 * キーは新しい方に揃え、消えたチャットの既読時刻を溜め込まない。
 */
function mergeChatLastSeen(stored: ChatLastSeen, lastSeen: ChatLastSeen): ChatLastSeen {
	const merged: Record<string, string> = {};
	for (const [chatId, seenAt] of Object.entries(lastSeen)) {
		const other = stored[chatId];
		merged[chatId] = other !== undefined && other > seenAt ? other : seenAt;
	}
	return merged;
}

/**
 * 既読時刻を保存する。localStorage へ書けなくてもセッション内キャッシュには残す。
 * セッション内キャッシュは読み直さないので、別タブが進めた既読を消さないよう保存値と突き合わせる。
 */
export function writeChatLastSeen(userId: string, lastSeen: ChatLastSeen): void {
	const stored = readStored(userId);
	const merged = stored === null ? lastSeen : mergeChatLastSeen(stored, lastSeen);
	sessionCache.set(userId, merged);
	try {
		window.localStorage.setItem(storageKey(userId), JSON.stringify(merged));
	} catch (err: unknown) {
		logStorageErrorOnce("writeChatLastSeen failed", err);
	}
}

/** 5秒ごとのポーリングで同じ内容を書き直さないための比較。 */
export function sameChatLastSeen(a: ChatLastSeen, b: ChatLastSeen): boolean {
	const keys = Object.keys(a);
	return keys.length === Object.keys(b).length && keys.every((chatId) => a[chatId] === b[chatId]);
}

/** テスト用にセッション内キャッシュを捨てる。 */
export function resetChatLastSeenCache(): void {
	sessionCache.clear();
	storageErrorLogged = false;
}
